import { useMemo } from "react";
import { EmptyState } from "@/components/ui/States";
import { type HeatScale, type MapCell } from "@/lib/api/map";
import { cellSize, describeAddress } from "@/lib/api/address";
import { qty } from "@/lib/format";
import { buildLadder, type Ladder } from "./ladder";
import { cellTone, type ColorMode, type HeatIndex } from "./tone";

/**
 * Qator chizmasi — stellajlar OLD TOMONDAN.
 *
 * Ombor xodimi stellajni aynan shunday ko'radi: qavatlar
 * ustma-ust, pastdan yuqoriga, har qavatda kataklar yonma-yon.
 * Reyestrdagi chizma ham shunday (manba faylning «Stellaj»
 * varag'i).
 *
 * Ilgari chizma «bitta qavatning yuqoridan ko'rinishi» edi:
 * satr — stellaj, ustun — yacheyka. U omborning haqiqiy
 * ko'rinishiga o'xshamasdi va «qator → qavat» ro'yxati bilan
 * birga xodimni chalkashtirardi.
 *
 * Nisbatlar HAQIQIY: qavat balandligi va katak eni reyestrdagi
 * o'lchamdan. Shuning uchun palletli qavat Y1 qavatidan to'rt
 * barobar baland ko'rinadi — xuddi omborda bo'lgani kabi.
 *
 * Qavatning o'rni esa butun qator bo'yicha bitta narvondan
 * olinadi (`ladder.ts`): 5-qavat hamma stellajda bir
 * balandlikda turadi, pastida javon bo'lmasa ham.
 */
export function AislePlan({
  cells,
  loading,
  selected,
  onSelect,
  mode,
  heat,
}: {
  cells: MapCell[];
  loading: boolean;
  selected: MapCell | null;
  onSelect: (cell: MapCell) => void;
  /** Rang nimani bildiradi: holat, ABC yoki harakat. */
  mode: ColorMode;
  heat?: { index: HeatIndex; scale: HeatScale };
}) {
  const plan = useMemo(() => buildPlan(cells), [cells]);

  if (loading) {
    return (
      <div className="map-canvas">
        <div className="skeleton" style={{ height: 320 }} />
      </div>
    );
  }
  if (plan.racks.length === 0) {
    return (
      <EmptyState
        icon="map"
        title="Chizma bo'sh"
        hint="Bu qatorda yacheyka yo'q yoki qator hali tanlanmagan."
      />
    );
  }

  const bodyHeight = plan.ladder.totalCm * PX_PER_CM;

  return (
    <div className="aisle-plan">
      {/* Qavat o'qi ALOHIDA ustun: raqamni stellaj ichiga qo'yish
          mayda yacheykani to'sib qo'yardi, har stellajda takrorlash
          esa chizmani raqamlar devoriga aylantirardi. O'q narvonning
          o'zidan chiziladi, shuning uchun yorliq har doim o'sha
          qavatning ro'parasida. */}
      <div className="rack rack-axis">
        <div className="rack-body" style={{ height: bodyHeight }}>
          {plan.ladder.slots.map((slot) => (
            <div
              className="rack-level"
              key={slot.level}
              style={{
                bottom: slot.bottomCm * PX_PER_CM,
                height: slot.heightCm * PX_PER_CM,
              }}
            >
              <span className="rack-axis-label">{slot.level}-qavat</span>
            </div>
          ))}
        </div>
        <div className="rack-foot">&nbsp;</div>
      </div>

      {plan.racks.map((rack) => (
        <div className="rack" key={rack.rack}>
          <div
            className="rack-body"
            style={{
              // O'lcham HAQIQIY: keng stellaj chizmada ham keng.
              // Balandlik esa HAMMA stellajda bir xil — qavatlar
              // qo'shni stellaj bilan bir chiziqda turishi uchun.
              width: rack.widthCm * PX_PER_CM,
              height: bodyHeight,
            }}
          >
            {/* Bo'sh o'rin KO'RINADI: stellajda 1-4 qavat
                bo'lmasa, uning o'rniga punktir javon chiziladi va
                5-qavat polga tushmaydi. */}
            {rack.gaps.map((gap) => (
              <div
                className="rack-gap"
                key={`gap-${gap.level}`}
                style={{
                  bottom: gap.bottomCm * PX_PER_CM,
                  height: gap.heightCm * PX_PER_CM,
                }}
              />
            ))}

            {rack.levels.map((level) => (
              <div
                className="rack-level"
                key={level.level}
                style={{
                  bottom: level.bottomCm * PX_PER_CM,
                  height: Math.max(level.heightCm * PX_PER_CM, 6),
                }}
              >
                {level.cells.map((cell) => {
                  const tone = cellTone(cell.cell, mode, heat);
                  return (
                    <button
                      type="button"
                      key={cell.location_id}
                      className={`rack-cell ${tone.key}${tone.mismatch ? " tone-mismatch" : ""}${
                        selected?.location_id === cell.location_id ? " selected" : ""
                      }`}
                      style={{ flexGrow: cell.width }}
                      onClick={() => onSelect(cell.cell)}
                      title={`${describeAddress(cell.cell.code)}\n${tone.label} · qoldiq ${qty(
                        cell.cell.on_hand_qty,
                      )} · ${cell.cell.distinct_sku_count} SKU`}
                    />
                  );
                })}
              </div>
            ))}
          </div>
          <div className="rack-foot">{rack.rack}-stellaj</div>
        </div>
      ))}
    </div>
  );
}

interface PlacedCell {
  location_id: string;
  cell: MapCell;
  /** Nisbiy eni — reyestrdagi o'lchamdan. */
  width: number;
}

interface PlanLevel {
  level: number;
  /** Poldan javon tagigacha, sm — narvondan. */
  bottomCm: number;
  /** Chiziladigan balandlik, sm. */
  heightCm: number;
  cells: PlacedCell[];
}

interface PlanGap {
  level: number;
  bottomCm: number;
  heightCm: number;
}

interface PlanRack {
  rack: number;
  levels: PlanLevel[];
  /** Stellajda yo'q qavatlarning o'rni. */
  gaps: PlanGap[];
  /** Eng keng qavatning eni, sm — stellaj kengligi shu. */
  widthCm: number;
}

/**
 * Santimetr → piksel.
 *
 * 0.22 — 190 sm li palletli qavat ~42 px bo'ladi: qoldiq rangi
 * ko'rinadi, 50 sm li Y1 qavati esa undan to'rt barobar past —
 * omborda qanday bo'lsa shunday.
 */
const PX_PER_CM = 0.22;

function buildPlan(cells: MapCell[]): { ladder: Ladder; racks: PlanRack[] } {
  const byRack = new Map<number, Map<number, PlacedCell[]>>();
  const measured: { rack: number; level: number; heightCm: number }[] = [];

  for (const cell of cells) {
    // `row` — stellaj, `col` — qavatdagi yacheyka (TZ §5.1).
    if (cell.row == null || cell.col == null || cell.level == null) continue;
    const size = cellSize(cell.cell_type, cell.length_cm, cell.width_cm, cell.height_cm);
    const levels = byRack.get(cell.row) ?? new Map<number, PlacedCell[]>();
    const list = levels.get(cell.level) ?? [];
    list.push({ location_id: cell.location_id, cell, width: size.length });
    levels.set(cell.level, list);
    byRack.set(cell.row, levels);
    measured.push({ rack: cell.row, level: cell.level, heightCm: size.height });
  }

  const ladder = buildLadder(measured);

  const racks = [...byRack.entries()]
    .sort(([a], [b]) => a - b)
    .map(([rack, levels]) => {
      const planLevels: PlanLevel[] = [];
      const gaps: PlanGap[] = [];
      // Pastdan yuqoriga: past qavatning balandroq katagi
      // ustidagi BO'SH o'rinni egallaydi va u yerga punktir
      // javon chizilmasligi kerak.
      let coveredToCm = 0;

      for (const slot of ladder.slots) {
        const list = levels.get(slot.level);
        if (list && list.length > 0) {
          const span = ladder.spanOf(rack, slot.level);
          planLevels.push({
            level: slot.level,
            bottomCm: slot.bottomCm,
            heightCm: span,
            cells: list.sort((a, b) => (a.cell.col ?? 0) - (b.cell.col ?? 0)),
          });
          coveredToCm = Math.max(coveredToCm, slot.bottomCm + span);
          continue;
        }
        if (slot.bottomCm >= coveredToCm) {
          gaps.push({ level: slot.level, bottomCm: slot.bottomCm, heightCm: slot.heightCm });
        }
      }

      return {
        rack,
        levels: planLevels,
        gaps,
        widthCm: Math.max(
          ...planLevels.map((level) =>
            level.cells.reduce((total, cell) => total + cell.width, 0),
          ),
          60,
        ),
      };
    });

  return { ladder, racks };
}
